import Link from 'next/link';
import { NextSeo } from 'next-seo';

import {
  DocsArticleLayout,
  DocsCallout,
  DocsCard,
  DocsCardGrid,
  DocsStep,
  DocsSteps,
  DocsTable,
} from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'three-screens', title: '1. Three screens, three questions' },
  { id: 'analytics', title: '2. Analytics' },
  { id: 'roi', title: '3. ROI & Metrics' },
  { id: 'knowledge-gaps', title: '4. Knowledge Gaps' },
  { id: 'the-loop', title: '5. The weekly loop' },
  { id: 'api', title: '6. Pulling the numbers out' },
];

const AnalyticsRoiPage = () => {
  return (
    <>
      <NextSeo
        title="Analytics, ROI & Knowledge Gaps"
        description="Read what your chatbot is actually doing: usage analytics, a configurable ROI model, and the Knowledge Gap detector that turns unanswered questions into content."
        canonical="https://chat.co/docs/analytics-roi"
        openGraph={{
          url: 'https://chat.co/docs/analytics-roi',
          title: 'Analytics, ROI & Knowledge Gaps | Chat.co',
          description:
            'Usage analytics, a configurable ROI model, and the Knowledge Gap detector that turns unanswered questions into content.',
          type: 'article',
          article: {
            publishedTime: '2026-08-30T00:00:00Z',
            modifiedTime: '2026-08-30T00:00:00Z',
            section: 'Operations',
            tags: ['analytics', 'roi', 'knowledge gaps', 'metrics'],
          },
        }}
        twitter={{ cardType: 'summary_large_image' }}
      />

      <DocsArticleLayout
        category="Operations"
        categoryColor="text-blue-700"
        title="Analytics, ROI &amp; knowledge gaps"
        description="What the bot did, what it was worth, and what it could not answer — and how to turn the third into content."
        readingTime="9 min read"
        lastUpdated="August 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'Lead capture',
          href: PageLink.docsLeadCapture,
        }}
        nextArticle={{
          title: 'Document Studio',
          href: PageLink.docsDocumentStudio,
        }}
        keyTakeaways={[
          'Analytics is account-wide; ROI and Knowledge Gaps are per bot',
          'The ROI figure is only as honest as the hourly rate and handling time you enter',
          'Knowledge Gaps finds questions the bot answered badly and drafts a Q&A pair for each',
          'Resolve a gap once you have fixed it, so the list stays a to-do rather than an archive',
          'Like and dislike rates are the cheapest quality signal you have — read the dislikes',
        ]}>
        <p className="lead">
          Three screens answer three different questions, and conflating them is how teams end up
          with dashboards nobody acts on. Analytics tells you what happened. ROI tells you what it
          was worth. Knowledge Gaps tells you what to do next — and it is the one that actually
          changes anything.
        </p>

        <section id="three-screens" className="scroll-mt-28">
          <h2>1. Three screens, three questions</h2>

          <DocsTable
            headers={['Screen', 'Where', 'Answers']}
            rows={[
              [
                <strong key="a">Analytics</strong>,
                'Main sidebar, account-wide',
                'How much is this being used, by whom, and are they coming back?',
              ],
              [
                <strong key="b">ROI &amp; Metrics</strong>,
                'Bot sidebar → Advanced',
                'What is this worth in hours and money?',
              ],
              [
                <strong key="c">Knowledge Gaps</strong>,
                'Bot sidebar → Advanced',
                'What did it fail to answer, and what should I write?',
              ],
            ]}
          />

          <DocsCallout variant="tip" title="If you only open one, open Knowledge Gaps">
            Analytics and ROI describe. Knowledge Gaps prescribes. Twenty minutes a week there does
            more for answer quality than any amount of dashboard-reading.
          </DocsCallout>
        </section>

        <section id="analytics" className="scroll-mt-28">
          <h2>2. Analytics</h2>
          <p>
            Account-level usage across all your bots — reachable from the main sidebar rather than
            inside a specific bot.
          </p>

          <DocsTable
            headers={['Metric', 'How to read it']}
            rows={[
              [
                <strong key="a">Unique users</strong>,
                'Reach. Rising means distribution is working — say nothing yet about quality.',
              ],
              [
                <strong key="b">New vs returning users</strong>,
                'The honest engagement signal. People come back to things that worked.',
              ],
              [
                <strong key="c">Return rate</strong>,
                'The same signal as a ratio, and the one to trend over months rather than days.',
              ],
              [
                <strong key="d">Average conversations per user</strong>,
                'Depth of use. A jump here often means the bot became genuinely useful to a subset.',
              ],
              [
                <strong key="e">Average session duration</strong>,
                'Deliberately ambiguous. Long sessions mean engagement or struggle — read transcripts to tell which.',
              ],
              [
                <strong key="f">Like / dislike rate</strong>,
                'Direct user judgement, and the cheapest quality metric you have.',
              ],
            ]}
          />

          <DocsCallout variant="note" title="The dislikes are the useful half">
            A like rate tells you little on its own. The disliked conversations are a pre-filtered
            list of your bot&apos;s worst moments, chosen by the people it failed. Read those before
            you read anything else on this page.
          </DocsCallout>

          <p>
            Usage is also broken down by model, and by which users are consuming the most message
            credits — useful when an allocation runs down faster than expected and you need to know
            where it went.
          </p>
        </section>

        <section id="roi" className="scroll-mt-28">
          <h2>3. ROI &amp; Metrics</h2>
          <p>
            Per bot, under <strong>Advanced</strong>. It converts conversations into hours saved and
            money saved, over Today, This week, This month, This year, Last 7 days or Last 30 days —
            each shown against the previous comparable period.
          </p>

          <h3>Configure it before you quote it</h3>
          <p>The model rests on two inputs you set yourself, plus a currency:</p>

          <DocsTable
            headers={['Input', 'What to put in it']}
            rows={[
              [
                <strong key="a">Hourly rate</strong>,
                'The fully-loaded hourly cost of whoever would otherwise handle these conversations — not their salary divided by 2,080.',
              ],
              [
                <strong key="b">Average human handling time</strong>,
                'How many minutes a person would really spend on one of these, in minutes. Measure a handful rather than guessing.',
              ],
            ]}
          />

          <DocsCallout variant="warning" title="This is your model, not a measurement">
            Chat.co multiplies your assumptions by real conversation volume. The volume is observed;
            the value is asserted. If you enter an optimistic rate and an optimistic handling time,
            you get an impressive number that will not survive its first serious question. Set them
            low enough that you would defend them to a finance team.
          </DocsCallout>

          <p>
            Conservative inputs also make the trend more useful, because a figure you believe is a
            figure you will act on when it moves.
          </p>
        </section>

        <section id="knowledge-gaps" className="scroll-mt-28">
          <h2>4. Knowledge Gaps</h2>
          <p>
            The most directly actionable screen in the product. It analyses real conversations and
            surfaces the questions your bot handled badly — the ones where it had nothing useful to
            retrieve.
          </p>

          <h3>The workflow</h3>
          <DocsSteps>
            <DocsStep title="Run the analysis">
              <p>
                Open <strong>Knowledge Gaps</strong> under Advanced. It works over real
                conversations, so it needs some traffic before it says anything interesting.
              </p>
            </DocsStep>
            <DocsStep title="Read the detected gaps">
              <p>
                Each one is a question a real person asked and did not get a good answer to. Some
                are genuinely out of scope; most are content you have not written yet.
              </p>
            </DocsStep>
            <DocsStep title="Ask for a suggested answer">
              <p>
                Chat.co drafts one from what it does know. Treat it as a starting point — you are
                the authority on your own business, and the draft is not.
              </p>
            </DocsStep>
            <DocsStep title="Create the Q&A pair">
              <p>
                One action turns the gap into a Q&amp;A pair in the knowledge base. The next person
                who asks gets the answer.
              </p>
            </DocsStep>
            <DocsStep title="Mark it resolved">
              <p>
                So the list stays a to-do rather than an archive. You can unresolve later if the
                answer turns out not to hold.
              </p>
            </DocsStep>
          </DocsSteps>

          <DocsCallout variant="tip" title="Not every gap deserves a Q&A pair">
            When several gaps circle the same topic, the right fix is usually one good document, not
            five Q&amp;A pairs. Pairs are for pinning an exact wording; documents are for covering a
            subject.
          </DocsCallout>

          <p>
            Knowledge Gaps and <Link href={PageLink.docsHumanHandoff}>handoff</Link> attack the same
            problem from opposite ends. A handoff is a failure a human caught; a gap is a failure
            nobody caught. Working both is what makes a bot get better over time instead of merely
            staying live.
          </p>
        </section>

        <section id="the-loop" className="scroll-mt-28">
          <h2>5. The weekly loop</h2>
          <p>
            Twenty minutes, once a week. This is the whole practice, and it beats any amount of
            one-off tuning:
          </p>

          <DocsSteps>
            <DocsStep title="Read the disliked conversations">
              <p>Start where users already told you something was wrong.</p>
            </DocsStep>
            <DocsStep title="Work the Knowledge Gaps list">
              <p>Create pairs for the sharp questions; note the topics that need a document.</p>
            </DocsStep>
            <DocsStep title="Skim the handoff notes">
              <p>
                Anything your team had to answer by hand more than once is content that should
                exist.
              </p>
            </DocsStep>
            <DocsStep title="Re-run your regression questions">
              <p>
                The short list from{' '}
                <Link href={PageLink.docsKnowledgeUpload}>the knowledge playbook</Link>. Confirm the
                week&apos;s additions did not displace something that was already working.
              </p>
            </DocsStep>
          </DocsSteps>
        </section>

        <section id="api" className="scroll-mt-28">
          <h2>6. Pulling the numbers out</h2>
          <p>
            All three surfaces are reachable programmatically with a secret key carrying{' '}
            <code>analytics:read</code>:
          </p>

          <ul>
            <li>
              <code>POST /client/v1/chatbots/:chatbotId/analytics</code> — analytics for one bot
            </li>
            <li>
              <code>GET /client/v1/chatbots/:chatbotId/knowledge-gaps</code> — detected gaps
            </li>
            <li>
              <code>GET /client/v1/message-allocation</code> — remaining message credits
            </li>
            <li>
              <code>GET /client/v1/chatbots/:chatbotId/leads</code> — captured leads
            </li>
          </ul>

          <p>
            Gaps can also be promoted into Q&amp;A pairs over the API, which makes an automated
            &ldquo;draft answers for last week&apos;s gaps and open a review queue&rdquo; job
            entirely practical. See the <Link href={PageLink.docsApiReference}>API reference</Link>.
          </p>

          <DocsCardGrid columns={2}>
            <DocsCard
              icon="tabler:upload"
              title="Knowledge upload playbook"
              href={PageLink.docsKnowledgeUpload}>
              Where the content you write here actually goes.
            </DocsCard>
            <DocsCard
              icon="tabler:quote"
              title="Citation quality"
              href={PageLink.docsCitationQuality}>
              When the bot answers but cites the wrong thing.
            </DocsCard>
          </DocsCardGrid>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default AnalyticsRoiPage;
